import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CustomEase } from 'gsap/CustomEase';
import { TextPlugin } from 'gsap/TextPlugin';
import { SplitText } from './lib/SplitText3';
import 'media-chrome';
import 'media-chrome/menu';
import 'media-chrome/media-theme-element';

import { lenis } from './common';
import { gsapCounter } from './lib/gsapEffects/gsapCounter';

import { initTopbar } from './components/topBar';
import { initGetStartedRed } from './components/getStartedRed';
import { initIntegrations } from './components/integrations';
import { initSectionScroller } from './components/sectionScroller';
import { initPricing } from './components/pricing';
import { initReviewsSlider } from './components/reviewsSlider';
import { initTrustedBy } from './components/trustedBy';
import { initWhoChoose } from './components/whoChoose';
import { initWhyChoose } from './components/whyChoose';
import { initGetStartedLight } from './components/getStartedLight';
import { initDetailsGroup } from './components/detailsGroup';
import { initTextInput } from './components/textInput';
import { initFooter } from './components/footer';
import { initTelInputs } from './components/telInput';
import { scheduleTask } from './lib/task';
import { initTooltips } from './components/tooltips';
import { initBlockCard01s } from './components/blockCard01';
import { initBlockCard02s } from './components/blockCard02';
import { initAllBlockCredits } from './components/blockCredits';
import { initKnowledgeBases } from './components/knowledgeBase';
import { initWaveBgs } from './components/waveBg';
import { initAiReports } from './components/aiReport';
import { initAllBlockClients } from './components/blockClients';
import { initBlockLeadForms } from './components/blockLeadForm';
import { initArticle } from './components/article';
import { initAllSideBySideCompares } from './components/sideBySideCompare';
import { initAllSubscribeBlocks } from './components/subscribeBlock';
import { initAllArticleSearchBlocks } from './components/articlesSearch';
import { initSelects } from './components/select';
import { initAllCaseStudyBlocks } from './components/blockCaseStudy';
import { initAllHowItWorksBlocks } from './components/blockHowItWorks';
import { initAllBlockVideosSliders } from './components/blockVideosSlider';
import { initAllBlockCards } from './components/blockCard';
import { initJsFluidMultipliers } from './lib/fluidSizings';
import { initAllSplineScenes } from './components/splineScene';
import { embedHsForm } from './lib/hsForms';
import { initAllLangSelects } from './components/langSelect';
import { initAllCollapsibleCards } from './components/blockCardCollapsible';
import { initAllCalendlyWidgets } from './components/calendly';
import { initAllCalcomWidgets } from './components/calcom';
import { initAllAccentCardsBlocks } from './components/blockAccentCards';
import { initAllLoomPlayers } from './components/loomPlayer';
import { initAllBlockMixedCards } from './components/blockMixedCards';
import { initAllBlockRedCtas } from './components/blockRedCta';
import { initAllBlockContactsMaps } from './components/blockContactsMap';
import { initAllBlockDepartments } from './components/blockDepartments';
import { initAllBlockDevelopersHub } from './components/blockDevelopersHub';
import { initInterfaceStates } from './lib/interfaceStates';
import { initCardsHero } from './components/cardsHero';
import { initAllTeamCarousels } from './components/teamCarousel';
import { initAllIndustries } from './components/industries';
import { initAllLargeImageHeros } from './components/largeImageHero';
import { initAllPromoHeroes } from './components/promoHero';
import { initAllLightAccentCardsBlocks } from './components/blockLightAccentCards';
import { initAllAccentReviews } from './components/accentReviews';
import { initAllBlockRedCta2 } from './components/blockRedCta2';
import { initTopbarMini } from './components/topBarMini';
import { initAllBlockCall } from './components/blockCall';
import { initAllChipCards } from './components/blockChipCards';
import { initAllGlossaryAlphabetSliders } from './components/glossaryAlphabet';
import { initAllRequestSentBooks } from './components/requestSentBook';
import { initAllRequestSentResources } from './components/requestSentResources';
import { initAllRequestSentSteps } from './components/requestSentSteps';
import { initAllWllBooks } from './components/wllBook';
import { initAllWllCards } from './components/wllCards';
import { initAllWllFinalCtas } from './components/wllFinalCta';
import { initAllWllHeroes } from './components/wllHero';
import { initGlossaryAlphabetJsTagNavigation } from './components/glossaryAlphabetNavigation';
import { attachResizeObserver } from './lib/resizeObserverWrapper';

if (import.meta.env?.VITE_TARGET === 'staticMarkup') {
  import('./targetStaticMarkup');
}

// Global js
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(TextPlugin);
gsap.registerPlugin(SplitText);
gsap.registerPlugin(CustomEase);
gsap.registerEffect(gsapCounter());

// Synchronize Lenis scrolling with GSAP's ScrollTrigger plugin
lenis.on('scroll', ScrollTrigger.update);

// Add Lenis's requestAnimationFrame (raf) method to GSAP's ticker
// This ensures Lenis's smooth scroll animation updates on each GSAP tick
gsap.ticker.add((time) => {
  lenis.raf(time * 1000); // Convert time from seconds to milliseconds
});

// Disable lag smoothing in GSAP to prevent any delay in scroll animations
gsap.ticker.lagSmoothing(0);

// Refresh ScrollTrigger after page's layout settles in
setTimeout(() => ScrollTrigger.refresh(), 500);

// Refresh ScrollTrigger on document.body resize
attachResizeObserver(function () {
  ScrollTrigger.refresh();
}, document.body);

initInterfaceStates();
initJsFluidMultipliers();
initAllSplineScenes();
initCardsHero();
embedHsForm('#hubspotFormContainer');
scheduleTask(initAllLargeImageHeros);
scheduleTask(initAllLoomPlayers);
scheduleTask(initAllCalendlyWidgets);
scheduleTask(initAllCalcomWidgets);
scheduleTask(initAllLangSelects);
scheduleTask(initTrustedBy);
scheduleTask(initIntegrations);
scheduleTask(initReviewsSlider);
scheduleTask(initWhoChoose);
scheduleTask(initWhyChoose);
scheduleTask(initPricing);
scheduleTask(initGetStartedLight);
scheduleTask(initGetStartedRed);
scheduleTask(initSectionScroller);
scheduleTask(initTopbar);
scheduleTask(initTopbarMini);
scheduleTask(initDetailsGroup);
scheduleTask(initTextInput);
scheduleTask(initTelInputs);
scheduleTask(initFooter);
scheduleTask(initTooltips);
scheduleTask(initBlockCard01s);
scheduleTask(initBlockCard02s);
scheduleTask(initAllBlockMixedCards);
scheduleTask(initAllLightAccentCardsBlocks);
scheduleTask(initAllAccentCardsBlocks);
scheduleTask(initAllBlockCards);
scheduleTask(initAllBlockRedCtas);
scheduleTask(initAllBlockRedCta2);
scheduleTask(initAllBlockCredits);
scheduleTask(initKnowledgeBases);
scheduleTask(initWaveBgs);
scheduleTask(initAiReports);
scheduleTask(initAllBlockClients);
scheduleTask(initBlockLeadForms);
scheduleTask(initArticle);
scheduleTask(initAllSideBySideCompares);
scheduleTask(initAllSubscribeBlocks);
scheduleTask(initAllArticleSearchBlocks);
scheduleTask(initAllCaseStudyBlocks);
scheduleTask(initAllHowItWorksBlocks);
scheduleTask(initAllBlockVideosSliders);
scheduleTask(initSelects);
scheduleTask(initAllCollapsibleCards);
scheduleTask(initAllBlockContactsMaps);
scheduleTask(initAllBlockDepartments);
scheduleTask(initAllBlockDevelopersHub);
scheduleTask(initAllTeamCarousels);
scheduleTask(initAllIndustries);
scheduleTask(initAllPromoHeroes);
scheduleTask(initAllAccentReviews);
scheduleTask(initAllBlockCall);
scheduleTask(initAllRequestSentBooks);
scheduleTask(initAllRequestSentResources);
scheduleTask(initAllRequestSentSteps);
scheduleTask(initAllWllBooks);
scheduleTask(initAllWllCards);
scheduleTask(initAllWllHeroes);
scheduleTask(initAllChipCards);
scheduleTask(initAllGlossaryAlphabetSliders);
scheduleTask(initAllWllFinalCtas);
scheduleTask(initGlossaryAlphabetJsTagNavigation);

console.log('🪐 Global JS initialized.');
